import { ALL_PATHS } from "../../../constants/menuItems";
import { DASHBOARD_REGISTRY } from "../../../constants/dashboardRegistry";
import { RESIDUAL_BUCKET_B_RULES } from "../../../constants/accessControlResidual";

// Frozen, byte-for-bit copy of PrivateRoute.tsx's pre-rewrite mapping - must NOT be "cleaned up"
// while shadow mode is active (see evaluateLegacyAccessControl below), or the divergence log stops
// meaning anything.
const PAGE_VIEW_MAPPING: Record<string, string> = {
  online_ce_performance: "digital_performance",
  ariya_experience_monitor: "ariya",
  seo_report: "seo_dashboard",
  digital_customer_experience: "digital_user_experience",
  mtm_beta: "mtm_metrics",
  mtm_metrics_that_matter: "mtm_metrics",
  online_to_physical_insights: "online_to_physical",
  car_configurator_g5: "configurator-g5",
  car_configurator_nibu: "configurator-nibu",
  pace_feature_activation_report: "pace",
  car_configurator_jp: "car_configurator",
  midas: "midas",
  sales: "global_ms_sales", // original
  // Global M&S Sales Tiers - specific access control
  reignite_tier_1: "reignite_user", // new
  reignite_tier_2: "reignite_manager", // new
  reignite_tier_3: "reignite_user", // new
  trim_mix_report: "trim_mix_report", // new
  // Analytics Tools - all mapped to streamlit view
  abt_data_query: "streamlit",
  correspondence_mapping: "streamlit",
  pricing_project: "streamlit",
  verbatim_analysis: "streamlit",
  visit_to_lead: "streamlit",
  serious_buyer: "streamlit",
};

export function parseViewsCookie(viewsCookie: string | undefined): string[] {
  const views: string[] = viewsCookie ? JSON.parse(viewsCookie) : [];

  if (views.includes("annotation_admin")) {
    views.push("admin");
  }

  return views;
}

// Extracted verbatim from PrivateRoute.tsx's pre-rewrite effect body (PAGE_VIEW_MAPPING/ALL_PATHS
// substring matching + the global_ms_sales/tier_* hardcoded branch) - a pure refactor for
// testability, not a bug fix. Existing quirks (e.g. ALL_PATHS's un-slashed "product_marketing_optimization"/
// "brand_performance" entries) are intentionally NOT corrected here; this must stay behaviorally
// frozen for as long as shadow mode is comparing against it.
export function evaluateLegacyAccessControl(currentPath: string, views: string[]): boolean {
  if (currentPath.includes("/global_ms_sales/tier_")) {
    let requiredView = "";

    if (currentPath.includes("/tier_one")) {
      requiredView = PAGE_VIEW_MAPPING["reignite_tier_1"];
    } else if (currentPath.includes("/tier_two")) {
      requiredView = PAGE_VIEW_MAPPING["reignite_tier_2"];
    } else if (currentPath.includes("/tier_three")) {
      requiredView = PAGE_VIEW_MAPPING["reignite_tier_3"];
    }

    if (requiredView) {
      return views.includes(requiredView);
    }
  }

  const availablePaths = views.reduce((result: string[], view) => {
    const paths = ALL_PATHS.filter((path) => {
      if (view in PAGE_VIEW_MAPPING) return path.includes(PAGE_VIEW_MAPPING[view]);

      return path.includes(view);
    });
    return [...result, ...paths];
  }, []);

  return !!availablePaths.find((value) => currentPath.includes(value));
}

export interface NewAccessResult {
  allowed: boolean;
  reason: "residual-bucket-b" | "registry-exact-match" | "no-match";
  matchedKey?: string;
  requiredInternalView?: string;
}

// Exact-match replacement: Bucket-B residual rules first (non-dashboard drilldowns that will never
// be registry rows), then an exact lookup against the (mock) `_dashboard` registry. No substring
// matching anywhere - every grant traces to one specific rule or row.
export function evaluateNewAccessControl(currentPath: string, views: string[]): NewAccessResult {
  const residualRule = RESIDUAL_BUCKET_B_RULES.find((rule) => rule.paths.includes(currentPath));
  if (residualRule) {
    return {
      allowed: views.includes(residualRule.internal_view),
      reason: "residual-bucket-b",
      requiredInternalView: residualRule.internal_view,
    };
  }

  const registryRow = DASHBOARD_REGISTRY.find((row) => row.path === currentPath);
  if (registryRow) {
    return {
      allowed: views.includes(registryRow.internal_view),
      reason: "registry-exact-match",
      matchedKey: registryRow.key,
      requiredInternalView: registryRow.internal_view,
    };
  }

  return { allowed: false, reason: "no-match" };
}

export function logAccessControlDivergence(currentPath: string, oldResult: boolean, newResult: NewAccessResult, views: string[]): void {
  console.warn(
    `[PrivateRoute shadow mode] Access control divergence on "${currentPath}": ` +
      `legacy=${oldResult}, new=${newResult.allowed} (reason=${newResult.reason}` +
      `${newResult.matchedKey ? `, matchedKey=${newResult.matchedKey}` : ""}` +
      `${newResult.requiredInternalView ? `, requiredInternalView=${newResult.requiredInternalView}` : ""})` +
      `, views=${JSON.stringify(views)}`
  );
}

// The only line that changes to make the registry-based logic authoritative. Flipped to true -
// evaluateLegacyAccessControl, PAGE_VIEW_MAPPING, and the divergence-logging block in
// PrivateRoute.tsx are all now dead code paths kept temporarily for the divergence log (still
// useful to catch any remaining real-world gap) and as an easy rollback (flip back to false)
// if something unexpected shows up post-flip. One known, accepted divergence going in: the 6
// legacy per-tool streamlit/mi_analytics view aliases (abt_data_query/correspondence_mapping/
// pricing_project/verbatim_analysis/visit_to_lead/serious_buyer) no longer over-grant on their
// own - a user holding only one of those old view names (not the shared streamlit/mi_analytics
// view) will see Access Denied on that dashboard until their views cookie is refreshed. See
// accessControl.test.ts's "OPEN QUESTION" test for the full detail; flip back to false immediately
// if this turns out to affect real users before their cookie refreshes on its own.
export const NEW_ACCESS_CONTROL_IS_AUTHORITATIVE = true;
